import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import useFeature from '@/lib/features';

type GetArtifactsResponseBody = paths['/artifacts']['get']['responses'][200]['content']['application/json'];
type GetArtifactsQuery = NonNullable<paths['/artifacts']['get']['parameters']['query']>;
type ArtifactsData = GetArtifactsResponseBody['data'];
type ArtifactType = NonNullable<GetArtifactsQuery['artifactTypes']>[number];

export type Artifact = ArtifactsData['artifacts'][number];

const ARTIFACT_CACHE_MS = 1000 * 60 * 5;

interface FetchArtifactsParams {
  accountId?: string;
  artifactTypes: ArtifactType[];
}

const fetchArtifacts = async ({ accountId, artifactTypes }: FetchArtifactsParams): Promise<Artifact[]> => {
  const response = await apiClient
    .get('api/supplier-experience/artifacts', {
      searchParams: qs.stringify(
        {
          artifactTypes,
          ...(accountId ? { accountId } : {}),
        } satisfies GetArtifactsQuery,
        { arrayFormat: 'brackets', indices: false }
      ),
    })
    .json<GetArtifactsResponseBody>();

  return response.data.artifacts;
};

// Query keys share an 'artifacts' prefix so a broad invalidate can clear both slices if ever needed.
type ArtifactsQueryKey = readonly ['artifacts', 'navigation' | 'marketing', string | null];

interface ArtifactsQueryOptions {
  /** React Query retry override. Omit to inherit the global default (3). */
  retry?: boolean | number;
}

/**
 * Shared `useQuery` for a single artifact type slice. Gated by
 * `enterprise-ui_useMarketingArtifacts`; consumers pick winners in `select`.
 */
const useArtifactsByType = <TData = Artifact[]>(
  queryKey: ArtifactsQueryKey,
  artifactTypes: ArtifactType[],
  accountId?: string | null,
  select?: (data: Artifact[]) => TData,
  options?: ArtifactsQueryOptions
) => {
  const [useArtifactsEnabled] = useFeature('enterprise-ui_useMarketingArtifacts');

  return useQuery({
    queryKey,
    queryFn: () => fetchArtifacts({ artifactTypes, accountId: accountId ?? undefined }),
    enabled: useArtifactsEnabled,
    throwOnError: false,
    select,
    staleTime: ARTIFACT_CACHE_MS,
    gcTime: ARTIFACT_CACHE_MS,
    ...(options?.retry !== undefined ? { retry: options.retry } : {}),
  });
};

/**
 * Fetches NAVIGATION artifacts (e.g. Fundbox `TERM_LOANS_BY_FUNDBOX` nav item).
 * Gated by `enterprise-ui_useMarketingArtifacts`. Consumers filter/pick winners in `select`
 * (e.g. `selectFundboxNavigationArtifact`).
 *
 * Long-lived per session: this slice is NOT invalidated by in-app actions (offer/draft
 * submit), so the side nav never refetches or flickers from those flows.
 *
 * This call gates app first paint via `ArtifactsGate` with `retry: 1`. On error it settles
 * (no data) without throwing, and the app paints as if no nav artifacts were returned
 * (nav without Fundbox).
 */
export const useNavigationArtifacts = <TData = Artifact[]>(
  accountId?: string | null,
  select?: (data: Artifact[]) => TData
) =>
  useArtifactsByType(['artifacts', 'navigation', accountId ?? null], ['NAVIGATION'], accountId, select, { retry: 1 });

/**
 * Fetches MARKETING_MESSAGE artifacts (e.g. homepage marketing widget).
 * Gated by `enterprise-ui_useMarketingArtifacts`. Consumers filter/pick winners in `select`
 * (e.g. `selectHighestPriorityMarketingArtifact`).
 *
 * Invalidate after actions that could change marketing artifact validity (draft/offer submit):
 * `invalidateQueries({ queryKey: ['artifacts', 'marketing', accountId] })` — this only refetches
 * the marketing widget, never the navigation slice.
 */
export const useMarketingArtifacts = <TData = Artifact[]>(
  accountId?: string | null,
  select?: (data: Artifact[]) => TData
) => useArtifactsByType(['artifacts', 'marketing', accountId ?? null], ['MARKETING_MESSAGE'], accountId, select);
